const MEASUREMENT_ID = import.meta.env.VITE_GA_MEASUREMENT_ID;

// Default Google Consent Mode: everything denied until user accepts
const DEFAULT_CONSENT = {
  ad_storage: 'denied',
  ad_user_data: 'denied',
  ad_personalization: 'denied',
  analytics_storage: 'denied',
};

let analyticsInitialized = false;

export const hasConsent = () => {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem('cookie_accepted') === '1';
  } catch {
    return false;
  }
};

const ensureGtag = () => {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  // Minimal shim to queue calls until gtag.js loads
  window.gtag = window.gtag || function gtag(){
    window.dataLayer.push(arguments);
  };
};

export const updateConsent = (granted = false) => {
  if (typeof window === 'undefined') return;
  ensureGtag();
  try{
    if (granted){
      window.gtag('consent', 'update', {
        ad_storage: 'granted',
        ad_user_data: 'granted',
        ad_personalization: 'granted',
        analytics_storage: 'granted',
      });
    } else {
      window.gtag('consent', 'update', DEFAULT_CONSENT);
    }
  }catch(e){ void e; }
};

export const initAnalytics = (force = false) => {
  if (typeof window === 'undefined') return;
  if (!MEASUREMENT_ID) return;
  if (!force && !hasConsent()) return;
  if (analyticsInitialized) return;

  ensureGtag();

  // Set default consent mode to denied before loading gtag script
  try{ window.gtag('consent', 'default', DEFAULT_CONSENT); }catch(e){ void e; }

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
  document.head.appendChild(script);

  window.gtag('js', new Date());
  window.gtag('config', MEASUREMENT_ID, {
    send_page_view: false,
  });

  // If we have consent, explicitly update to granted; this will enable storage.
  try{ if (hasConsent() || force) updateConsent(true); }catch(e){ void e; }

  analyticsInitialized = true;
};

export const disableAnalytics = () => {
  if (typeof window === 'undefined') return;
  try{ updateConsent(false); }catch(e){ void e; }
  // Remove GA cookies (best effort). Iterate over cookies and remove names matching common GA prefixes
  try{
    const cookies = document.cookie ? document.cookie.split(';') : [];
    const patterns = [/^_ga/, /^_gid/, /^_gat/, /^_gac/, /^_gcl_/, /^ga_/i, /^gac_/i, /^_ga_/, /^__utm/, /^_dc_gtm_/, /^AMP_TOKEN/, /^G_ENABLED_ID/];
    cookies.forEach(c => {
      const name = c.split('=')[0].trim();
      if(patterns.some(p => p.test(name))){
        // Try removing with different domain permutations
        const domains = [window.location.hostname, '.' + window.location.hostname];
        domains.forEach(d => {
          try{ document.cookie = `${name}=; Path=/; Domain=${d}; Expires=Thu, 01 Jan 1970 00:00:01 GMT;`; }catch(e){ void e; }
        });
        // Also try without domain
        try{ document.cookie = `${name}=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;`; }catch(e){ void e; }
      }
    });
  }catch(e){ void e; }

  // Remove gtag script if loaded
  try{
    const scripts = Array.from(document.getElementsByTagName('script'));
    scripts.forEach(s => {
      if(s.src && s.src.indexOf('googletagmanager.com/gtag/js') !== -1){
        s.parentNode && s.parentNode.removeChild(s);
      }
    });
  }catch(e){ void e; }

  // Reset global objects and flags
  try{ window.gtag = () => {}; }catch(e){ void e; }
  try{ window.dataLayer = []; }catch(e){ void e; }
  analyticsInitialized = false;
};

export const trackPageView = (path, title) => {
  if (typeof window === 'undefined') return;
  if (!MEASUREMENT_ID) return;
  if (!analyticsInitialized && !window.gtag) return;

  ensureGtag();

  const pagePath = path || window.location.pathname + window.location.search + window.location.hash;
  const pageLocation = window.location.origin + pagePath;

  window.gtag('event', 'page_view', {
    page_path: pagePath,
    page_title: title || document.title,
    page_location: pageLocation,
  });
};

export const trackEvent = (eventName, params = {}) => {
  if (typeof window === 'undefined') return;
  if (!MEASUREMENT_ID) return;
  if (!analyticsInitialized && !window.gtag) return;

  ensureGtag();

  window.gtag('event', eventName, params);
};

export const trackEcommerceEvent = (eventName, params = {}) => {
  if (typeof window === 'undefined') return;
  if (!MEASUREMENT_ID) return;
  if (!analyticsInitialized && !window.gtag) return;

  ensureGtag();

  const payload = { ...params };
  if (Array.isArray(payload.items)) {
    payload.items = payload.items
      .map((item, index) => {
        if (!item) return null;
        const itemId = item.item_id || item.id || item.sku;
        const itemName = item.item_name || item.name || item.title;
        if (!itemId || !itemName) return null;
        const quantityValue = Number(item.quantity);
        const priceValue = Number(item.price);
        const cleaned = {
          item_id: String(itemId),
          item_name: String(itemName),
          index: item.index != null ? item.index : index,
        };
        cleaned.quantity = Number.isFinite(quantityValue) && quantityValue > 0 ? quantityValue : 1;
        if (Number.isFinite(priceValue)) cleaned.price = priceValue;
        if (item.item_category) cleaned.item_category = String(item.item_category);
        if (item.item_list_name) cleaned.item_list_name = String(item.item_list_name);
        if (item.item_list_id) cleaned.item_list_id = String(item.item_list_id);
        if (item.item_variant) cleaned.item_variant = String(item.item_variant);
        if (item.item_brand) cleaned.item_brand = String(item.item_brand);
        return cleaned;
      })
      .filter(Boolean);
    if (!payload.items.length) {
      delete payload.items;
    }
  }

  window.gtag('event', eventName, payload);
};
